/**
 * Widget Error Boundary (Story 5.5, Task 1.5)
 *
 * React error boundary that catches render errors per widget
 * and displays a graceful fallback instead of crashing the entire canvas.
 */

import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";

interface WidgetErrorBoundaryProps {
  widgetTitle: string;
  children: ReactNode;
}

interface WidgetErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class WidgetErrorBoundary extends Component<
  WidgetErrorBoundaryProps,
  WidgetErrorBoundaryState
> {
  constructor(props: WidgetErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): WidgetErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `[WidgetErrorBoundary] ${this.props.widgetTitle} failed to render:`,
      error,
      errorInfo
    );
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          data-testid="widget-error-boundary"
          className="h-full flex flex-col items-center justify-center p-3 text-center"
        >
          <span className="text-destructive text-sm font-medium mb-1">
            Widget failed to render
          </span>
          <span className="text-xs text-muted-foreground mb-2">
            {this.props.widgetTitle}
          </span>
          <button
            onClick={this.handleRetry}
            className="text-xs text-primary hover:underline"
            data-testid="widget-error-retry"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
